import { applyRate, rentalDays, sumMinor } from '@zuruflex/utils';
import { promoDiscountAmount } from './promotions.js';
/**
 * Fallback tax rate, applied to the rental subtotal. Deposits are untaxed.
 *
 * This is Ghana's rate and is only correct for Ghana — Rwanda is 18%. It
 * exists so a quote is never priced at zero tax when settings have not
 * loaded; the real rate comes from the `tax.rate` setting, scoped to the
 * branch's country. Do not treat it as the rate.
 */
export const DEFAULT_TAX_RATE = 0.15;
export const DEFAULT_DISCOUNT_POLICY = {
    weeklyRate: 0.1,
    monthlyRate: 0.2,
    weeklyFromDays: 7,
    monthlyFromDays: 28,
};
/**
 * The single place a price is computed. The API, the admin UI and the WhatsApp
 * flow all call this, so a customer is never quoted one number and charged
 * another.
 *
 * Long-hire discounts are applied automatically: a week is cheaper per day
 * than a weekend, which is what customers already expect from rental firms.
 */
export function quoteBooking(input) {
    const days = rentalDays(input.pickupAt, input.dropoffAt);
    const taxRate = input.taxRate ?? DEFAULT_TAX_RATE;
    const base = input.dailyRate * days;
    const lines = [
        { labelKey: 'priceLine.base', labelArgs: { count: days }, amount: base, kind: 'BASE' },
    ];
    for (const extra of input.extras ?? []) {
        lines.push({
            // An extra's name is supplied by the caller, so it passes through as
            // an argument rather than becoming a key of its own.
            labelKey: 'priceLine.extra',
            labelArgs: { label: extra.label },
            amount: extra.perDay ? extra.amount * days : extra.amount,
            kind: 'EXTRA',
        });
    }
    /*
     * Inside the subtotal, so Zuruflex's commission applies to it as it does to the
     * rental, and so it is taxed. Deliberately outside the long-hire discount
     * below, which is computed off `base` alone: a week is cheaper per day than
     * a weekend, but a driver still works every day of it.
     */
    if (input.driverDailyRate) {
        lines.push({
            labelKey: 'priceLine.driver',
            labelArgs: { count: days },
            amount: input.driverDailyRate * days,
            kind: 'EXTRA',
        });
    }
    const durationRate = longHireDiscountRate(days, input.discountPolicy);
    const explicitRate = input.discountRate ?? 0;
    const discountRate = Math.max(durationRate, explicitRate);
    const rateAmount = applyRate(base, discountRate);
    /*
     * One discount, never two. Compared by amount rather than by rate because
     * a fixed-amount promo has no rate, and because "which saves the renter
     * more on *this* booking" is the question — a ₵100 code beats 10% off a
     * ₵600 week and loses to it on a ₵2,000 one. A tie goes to the long-hire
     * discount, which costs nobody a redemption.
     */
    const promoAmount = input.promo ? promoDiscountAmount(input.promo, base) : 0;
    let promo;
    let promoRejected;
    if (input.promo && promoAmount > rateAmount) {
        promo = {
            promotionId: input.promo.promotionId,
            code: input.promo.code,
            name: input.promo.name ?? null,
            labelKey: 'priceLine.promo',
            amount: promoAmount,
            funder: input.promo.funder,
        };
        lines.push({
            labelKey: promo.labelKey,
            labelArgs: { code: input.promo.code ?? input.promo.name ?? '' },
            amount: -promoAmount,
            kind: 'DISCOUNT',
        });
    }
    else {
        if (input.promo && rateAmount > 0)
            promoRejected = 'beatenByLongHire';
        if (discountRate > 0) {
            lines.push({
                labelKey: discountRate === durationRate ? 'priceLine.longHireDiscount' : 'priceLine.discount',
                labelArgs: { percent: Math.round(discountRate * 100) },
                amount: -rateAmount,
                kind: 'DISCOUNT',
            });
        }
    }
    const subtotal = sumMinor(lines.map((line) => line.amount));
    const tax = applyRate(subtotal, taxRate);
    lines.push({
        labelKey: 'priceLine.tax',
        labelArgs: { percent: Math.round(taxRate * 100) },
        amount: tax,
        kind: 'TAX',
    });
    const depositDue = input.depositAmount ?? 0;
    if (depositDue > 0) {
        lines.push({ labelKey: 'priceLine.deposit', amount: depositDue, kind: 'DEPOSIT' });
    }
    return {
        days,
        currency: input.currency,
        lines,
        subtotal,
        tax,
        total: subtotal + tax + depositDue,
        depositDue,
        ...(promo ? { promo } : {}),
        ...(promoRejected ? { promoRejected } : {}),
    };
}
/**
 * The automatic discount for a hire of `days`.
 *
 * Thresholds and rates are operator-tunable, so the monthly band is only
 * checked first because it is the longer one — an operator who sets the
 * monthly threshold below the weekly one still gets the longer band's
 * rate, rather than a silently unreachable setting.
 */
export function longHireDiscountRate(days, policy = DEFAULT_DISCOUNT_POLICY) {
    const bands = [
        { fromDays: policy.monthlyFromDays, rate: policy.monthlyRate },
        { fromDays: policy.weeklyFromDays, rate: policy.weeklyRate },
    ]
        .filter((band) => days >= band.fromDays)
        .map((band) => band.rate);
    return bands.length ? Math.max(...bands) : 0;
}
